import { useMemo, useState } from 'react'
import {
  Check,
  Copy,
  KeyRound,
  Pencil,
  Plus,
  Search,
  ShieldAlert,
  Trash2,
  UserRound,
  UserX,
} from 'lucide-react'

import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Checkbox, Field, Input, Select } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { useSession } from '@/features/auth/api'
import {
  useAdminUsers,
  useCreateUser,
  useDeleteUser,
  useResetUserPassword,
  useUpdateUser,
  type AdminUser,
  type UserStatus,
} from '@/features/admin/api'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'

const roleLabels: Record<string, string> = {
  student: 'Peserta',
  expert: 'Tenaga Ahli',
  teaching_assistant: 'Asisten Pengajar',
  client: 'Klien',
  employer: 'Perekrut',
  admin_ops: 'Admin Ops',
  mediator: 'Mediator',
  finance: 'Finance',
  super_admin: 'Super Admin',
}

/**
 * Peran internal hanya dapat diberikan Super Admin. Daftar ini menyamai
 * daftar di peladen; yang menahan pemberiannya tetap peladen, dan yang di
 * sini hanya menghindarkan admin dari menekan tombol yang pasti ditolak.
 */
const internalRoles = ['admin_ops', 'mediator', 'finance', 'super_admin']

const statusLabels: Record<UserStatus, string> = {
  active: 'Aktif',
  suspended: 'Nonaktif',
  deleted: 'Dihapus',
}

const statusTone: Record<UserStatus, 'success' | 'warning' | 'danger'> = {
  active: 'success',
  suspended: 'warning',
  deleted: 'danger',
}

export function AdminUsersPage() {
  const [q, setQ] = useState('')
  const [cari, setCari] = useState('')
  const [status, setStatus] = useState('')
  const [role, setRole] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useAdminUsers({ q: cari, status, role, page })
  const users = data?.users ?? []
  const meta = data?.meta

  const [formulir, setFormulir] = useState<{ open: boolean; user: AdminUser | null }>({
    open: false,
    user: null,
  })

  const terapkanCari = () => {
    setCari(q.trim())
    setPage(1)
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Pengguna</h1>
          <p className="mt-1 text-muted">
            Tambah akun, atur peran, setel ulang kata sandi, dan nonaktifkan yang tidak lagi berhak masuk.
          </p>
        </div>
        <Button onClick={() => setFormulir({ open: true, user: null })}>
          <Plus className="size-4" />
          Tambah pengguna
        </Button>
      </header>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && terapkanCari()}
            onBlur={terapkanCari}
            placeholder="Cari nama, email, atau nomor telepon"
            className="pl-10"
            aria-label="Cari pengguna"
          />
        </div>

        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          aria-label="Saring menurut keadaan"
          className="lg:w-48"
        >
          <option value="">Semua keadaan</option>
          <option value="active">Aktif</option>
          <option value="suspended">Nonaktif</option>
          <option value="deleted">Dihapus</option>
        </Select>

        <Select
          value={role}
          onChange={(e) => {
            setRole(e.target.value)
            setPage(1)
          }}
          aria-label="Saring menurut peran"
          className="lg:w-52"
        >
          <option value="">Semua peran</option>
          {Object.entries(roleLabels).map(([nilai, label]) => (
            <option key={nilai} value={nilai}>
              {label}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : users.length === 0 ? (
        <Card>
          <EmptyState
            illustration="box"
            icon={<UserRound className="size-6" />}
            title="Tidak ada pengguna yang cocok"
            description={cari || status || role ? 'Coba longgarkan pencarian atau saringannya.' : 'Belum ada akun terdaftar.'}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {users.map((user) => (
            <BarisPengguna key={user.id} user={user} onSunting={() => setFormulir({ open: true, user })} />
          ))}
        </div>
      )}

      {meta && meta.total_pages > 1 ? (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted">
            Halaman {meta.page} dari {meta.total_pages} · {meta.total} pengguna
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={meta.page <= 1} onClick={() => setPage(meta.page - 1)}>
              Sebelumnya
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={meta.page >= meta.total_pages}
              onClick={() => setPage(meta.page + 1)}
            >
              Berikutnya
            </Button>
          </div>
        </div>
      ) : null}

      <FormulirPengguna
        key={formulir.user?.id ?? 'baru'}
        open={formulir.open}
        user={formulir.user}
        onClose={() => setFormulir({ open: false, user: null })}
      />
    </div>
  )
}

function BarisPengguna({ user, onSunting }: { user: AdminUser; onSunting: () => void }) {
  const { data: session } = useSession()
  const diriSendiri = session?.user.id === user.id

  const [konfirmasiHapus, setKonfirmasiHapus] = useState(false)
  const [konfirmasiSandi, setKonfirmasiSandi] = useState(false)

  const simpan = useUpdateUser()
  const hapus = useDeleteUser()
  const toast = useToast()

  const ubahStatus = () => {
    const status: UserStatus = user.status === 'active' ? 'suspended' : 'active'
    simpan.mutate(
      { id: user.id, status },
      {
        onSuccess: () => toast.success(status === 'active' ? 'Akun diaktifkan' : 'Akun dinonaktifkan'),
        onError: (error) => toast.error('Gagal mengubah keadaan akun', pesan(error)),
      },
    )
  }

  return (
    <Card>
      <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Avatar name={user.full_name} src={user.avatar_url} size="lg" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-bold text-ink">{user.full_name}</p>
            <Badge tone={statusTone[user.status]}>{statusLabels[user.status]}</Badge>
            {diriSendiri ? <Badge tone="info">Anda</Badge> : null}
            {!user.email_verified ? <Badge tone="neutral">Surel belum terverifikasi</Badge> : null}
          </div>

          <p className="mt-0.5 truncate text-sm text-muted">
            {user.email}
            {user.phone ? ` · ${user.phone}` : ''}
          </p>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {user.roles.length === 0 ? (
              <span className="mono-label text-[10px] text-subtle">Tanpa peran</span>
            ) : (
              user.roles.map((peran) => (
                <span
                  key={peran}
                  className="rounded-full bg-sunken px-2 py-0.5 text-[11px] font-semibold text-muted"
                >
                  {roleLabels[peran] ?? peran}
                </span>
              ))
            )}
          </div>

          <p className="mono-label mt-2 text-[10px] text-subtle">Bergabung {formatDate(user.created_at)}</p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setKonfirmasiSandi(true)}>
            <KeyRound className="size-4" />
            <span className="hidden sm:inline">Kata sandi</span>
          </Button>

          {/* Admin tidak dapat mengunci dirinya sendiri keluar. Peladen
              menolaknya juga; tombol yang dimatikan di sini hanya
              menghindarkan admin dari pesan galat yang tidak perlu. */}
          <Button
            size="sm"
            variant="outline"
            onClick={ubahStatus}
            disabled={diriSendiri || simpan.isPending || user.status === 'deleted'}
            title={diriSendiri ? 'Anda tidak dapat menonaktifkan akun sendiri' : undefined}
          >
            <UserX className="size-4" />
            <span className="hidden sm:inline">{user.status === 'active' ? 'Nonaktifkan' : 'Aktifkan'}</span>
          </Button>

          <Button size="sm" variant="outline" onClick={onSunting} aria-label="Sunting pengguna">
            <Pencil className="size-4" />
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => setKonfirmasiHapus(true)}
            disabled={diriSendiri || user.status === 'deleted'}
            aria-label="Hapus pengguna"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </CardBody>

      <DialogHapus
        open={konfirmasiHapus}
        user={user}
        memproses={hapus.isPending}
        onClose={() => setKonfirmasiHapus(false)}
        onHapus={() =>
          hapus.mutate(user.id, {
            onSuccess: () => {
              setKonfirmasiHapus(false)
              toast.success('Akun dihapus')
            },
            onError: (error) => toast.error('Gagal menghapus', pesan(error)),
          })
        }
      />

      <DialogKataSandi open={konfirmasiSandi} user={user} onClose={() => setKonfirmasiSandi(false)} />
    </Card>
  )
}

function DialogHapus({
  open,
  user,
  memproses,
  onClose,
  onHapus,
}: {
  open: boolean
  user: AdminUser
  memproses: boolean
  onClose: () => void
  onHapus: () => void
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Hapus akun ini?"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button variant="danger" onClick={onHapus} disabled={memproses}>
            {memproses ? 'Menghapus…' : 'Hapus akun'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="font-semibold text-ink">
          {user.full_name} · {user.email}
        </p>
        <p className="text-muted">
          Akses akun ini dicabut seketika dan seluruh perangkatnya keluar. Pemiliknya tidak dapat masuk lagi.
        </p>
        {/* Alasannya disebutkan, bukan disembunyikan: admin yang mengira
            datanya benar-benar terhapus akan salah menjawab bila suatu hari
            pemiliknya menanyakan riwayat transaksinya. */}
        <p className="rounded-xl bg-sunken px-3 py-2 text-muted">
          Riwayat pesanan, kontrak, dan catatan keuangannya tetap tersimpan. Data itu dirujuk pembukuan yang wajib
          dapat ditelusuri, jadi tidak dapat ikut dihapus.
        </p>
      </div>
    </Dialog>
  )
}

function DialogKataSandi({ open, user, onClose }: { open: boolean; user: AdminUser; onClose: () => void }) {
  const [sandi, setSandi] = useState('')
  const [hasil, setHasil] = useState<{ password?: string; sessionsEnded: boolean } | null>(null)
  const [galat, setGalat] = useState('')
  const [disalin, setDisalin] = useState(false)

  const setel = useResetUserPassword()

  const tutup = () => {
    setSandi('')
    setHasil(null)
    setGalat('')
    setDisalin(false)
    onClose()
  }

  const kirim = () => {
    setGalat('')
    setel.mutate(
      { id: user.id, password: sandi },
      {
        onSuccess: (data) =>
          setHasil({ password: data.generated_password, sessionsEnded: data.sessions_ended ?? true }),
        onError: (error) => setGalat(pesan(error)),
      },
    )
  }

  const salin = async () => {
    if (!hasil?.password) return
    await navigator.clipboard.writeText(hasil.password)
    setDisalin(true)
  }

  return (
    <Dialog
      open={open}
      onClose={tutup}
      title="Setel ulang kata sandi"
      description={`${user.full_name} · ${user.email}`}
      footer={
        hasil ? (
          <Button onClick={tutup}>Selesai</Button>
        ) : (
          <>
            <Button variant="outline" onClick={tutup}>
              Batal
            </Button>
            <Button onClick={kirim} disabled={setel.isPending}>
              {setel.isPending ? 'Menyetel…' : 'Setel kata sandi'}
            </Button>
          </>
        )
      }
    >
      {hasil ? (
        <div className="space-y-4">
          {hasil.password ? (
            <Field
              label="Kata sandi baru"
              hint="Hanya ditampilkan sekali. Salin dan sampaikan lewat jalur yang aman — jangan lewat pesan yang tersimpan selamanya."
            >
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-xl border border-line bg-sunken px-3 py-2.5 font-mono text-sm text-ink">
                  {hasil.password}
                </code>
                <Button variant="outline" onClick={salin} aria-label="Salin kata sandi">
                  {disalin ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </Field>
          ) : (
            <p className="text-sm text-muted">Kata sandi yang Anda tentukan sudah tersimpan.</p>
          )}

          {hasil.sessionsEnded ? (
            <p className="text-sm text-muted">
              Seluruh sesi akun ini dicabut, jadi perangkat yang tadinya masih masuk harus masuk ulang.
            </p>
          ) : (
            /* Kegagalan pencabutan tidak boleh disembunyikan: admin yang
               mengira akunnya sudah aman tidak akan menindaklanjuti. */
            <p className="flex gap-2 rounded-xl bg-danger-soft px-3 py-2.5 text-sm font-medium text-danger">
              <ShieldAlert className="mt-0.5 size-4 shrink-0" />
              <span>
                Sesi lama gagal dicabut — penyimpan sesi tidak dapat dihubungi. Perangkat yang tadinya masih masuk
                tetap dapat memakai akun ini sampai sesinya kedaluwarsa. Coba setel ulang sekali lagi.
              </span>
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {galat ? (
            <p className="rounded-xl bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
              {galat}
            </p>
          ) : null}

          <Input
            label="Kata sandi baru"
            type="password"
            value={sandi}
            onChange={(e) => setSandi(e.target.value)}
            placeholder="Kosongkan agar dibuatkan acak"
            hint="Minimal 8 karakter. Kosongkan dan sistem akan membuatkan yang kuat."
          />

          <p className="text-sm text-muted">
            Seluruh sesi akun ini akan dicabut. Pemiliknya harus masuk ulang di semua perangkat.
          </p>
        </div>
      )}
    </Dialog>
  )
}

function FormulirPengguna({
  open,
  user,
  onClose,
}: {
  open: boolean
  user: AdminUser | null
  onClose: () => void
}) {
  const { data: session } = useSession()
  const bolehPeranInternal = useMemo(
    () => (session?.user.roles ?? []).includes('super_admin'),
    [session],
  )

  const [nama, setNama] = useState(user?.full_name ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [telepon, setTelepon] = useState(user?.phone ?? '')
  const [sandi, setSandi] = useState('')
  const [peran, setPeran] = useState<string[]>(user?.roles ?? ['student'])
  const [galat, setGalat] = useState('')
  const [sandiBaru, setSandiBaru] = useState('')

  const buat = useCreateUser()
  const ubah = useUpdateUser()
  const toast = useToast()
  const menyimpan = buat.isPending || ubah.isPending

  const diriSendiri = session?.user.id === user?.id

  const alihkanPeran = (nilai: string) =>
    setPeran((kini) => (kini.includes(nilai) ? kini.filter((p) => p !== nilai) : [...kini, nilai]))

  const kirim = () => {
    setGalat('')

    if (user) {
      ubah.mutate(
        {
          id: user.id,
          full_name: nama,
          phone: telepon,
          // Peran akun sendiri tidak pernah ikut dikirim: peladen
          // menolaknya, dan menyertakannya hanya menghasilkan galat.
          ...(diriSendiri ? {} : { roles: peran }),
        },
        {
          onSuccess: () => {
            toast.success('Perubahan tersimpan')
            onClose()
          },
          onError: (error) => setGalat(pesan(error)),
        },
      )
      return
    }

    buat.mutate(
      { email, full_name: nama, phone: telepon, password: sandi || undefined, roles: peran },
      {
        onSuccess: (data) => {
          if (data.generated_password) {
            setSandiBaru(data.generated_password)
          } else {
            toast.success('Pengguna dibuat')
            onClose()
          }
        },
        onError: (error) => setGalat(pesan(error)),
      },
    )
  }

  if (sandiBaru) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title="Pengguna dibuat"
        description={`${nama} · ${email}`}
        footer={<Button onClick={onClose}>Selesai</Button>}
      >
        <Field
          label="Kata sandi awal"
          hint="Hanya ditampilkan sekali. Sampaikan lewat jalur yang aman, dan minta pemiliknya menggantinya setelah masuk."
        >
          <code className="block rounded-xl border border-line bg-sunken px-3 py-2.5 font-mono text-sm text-ink">
            {sandiBaru}
          </code>
        </Field>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={user ? 'Sunting pengguna' : 'Tambah pengguna'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button onClick={kirim} disabled={menyimpan}>
            {menyimpan ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <p className="rounded-xl bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
            {galat}
          </p>
        ) : null}

        <Input
          label="Nama lengkap"
          required
          value={nama}
          onChange={(e) => setNama(e.target.value)}
          placeholder="Budi Santoso"
        />

        <Input
          label="Email"
          type="email"
          required={!user}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={Boolean(user)}
          hint={user ? 'Email tidak dapat diubah — ia dipakai sebagai identitas akun di seluruh riwayat.' : undefined}
          placeholder="nama@contoh.id"
        />

        <Input
          label="Nomor telepon"
          value={telepon}
          onChange={(e) => setTelepon(e.target.value)}
          placeholder="08xxxxxxxxxx"
        />

        {!user ? (
          <Input
            label="Kata sandi awal"
            type="password"
            value={sandi}
            onChange={(e) => setSandi(e.target.value)}
            placeholder="Kosongkan agar dibuatkan acak"
            hint="Minimal 8 karakter. Kosongkan dan sistem akan membuatkan yang kuat."
          />
        ) : null}

        <Field
          label="Peran"
          hint={
            diriSendiri
              ? 'Peran akun sendiri tidak dapat diubah dari sini. Minta admin lain yang melakukannya.'
              : bolehPeranInternal
                ? 'Peran internal menentukan akses ke halaman pengelolaan.'
                : 'Peran internal hanya dapat diberikan Super Admin.'
          }
        >
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.entries(roleLabels).map(([nilai, label]) => {
              const terkunci = diriSendiri || (internalRoles.includes(nilai) && !bolehPeranInternal)

              return (
                <Checkbox
                  key={nilai}
                  label={label}
                  checked={peran.includes(nilai)}
                  disabled={terkunci}
                  onChange={() => alihkanPeran(nilai)}
                />
              )
            })}
          </div>
        </Field>
      </div>
    </Dialog>
  )
}

function pesan(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Terjadi kesalahan yang tidak terduga.'
}
